<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import Badge from '@/components/ui/Badge.vue';
import UISelect from '@/components/ui/UISelect.vue';
import Combobox from '@/components/v2/Combobox.vue';
import type { ComboboxOption } from '@/components/v2/Combobox.vue';
import { Clock, Eye, X } from '@lucide/vue';
import type { Document } from '@/types/ui/PromptManager';
import { documentService } from '@/services/v2/DocumentService';
import { accountService } from '@/services/api';
import type { AccountSummary } from '@/types/AccountSummary';
import type { DocumentType, Document as PlatformDocument } from '@/types/v2/Document';
import DocumentViewerModal from '@/components/DocumentViewerModal.vue';

interface Props {
  selectedDocument: Document | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  documentSelect: [document: Document | null];
}>();

// State
const accounts = ref<AccountSummary[]>([]);
const documentTypes = ref<DocumentType[]>([]);
const documents = ref<PlatformDocument[]>([]);
const allAccountDocuments = ref<PlatformDocument[]>([]); // All documents for selected account (for filtering)
const selectedAccountId = ref<string>('');
const selectedDocumentTypeId = ref<string>('');
const isLoadingAccounts = ref(false);
const isLoadingDocumentTypes = ref(false);
const isLoadingDocuments = ref(false);
const showDocumentModal = ref(false);
const viewDocumentFileName = ref('');
const viewDocument = ref<PlatformDocument | undefined>(undefined);

// Computed
const accountOptions = computed<ComboboxOption[]>(() => {
  return accounts.value.map((account) => ({
    label: account.accountName,
    value: account.accountID.toString(),
  }));
});

const policyDocumentTypes = computed(() => {
  return documentTypes.value.filter((type) => type.domainName === 'Policy').sort((a, b) => a.name.localeCompare(b.name));
});

// Filter document types to only those that appear in the allAccountDocuments list
const availableDocumentTypes = computed(() => {
  if (allAccountDocuments.value.length === 0) {
    return [];
  }

  // Get unique document type IDs from all account documents (not filtered)
  const documentTypeIdsInDocuments = new Set(
    allAccountDocuments.value.filter((doc) => doc.documentDomainName === 'Policy' && doc.documentTypeId).map((doc) => doc.documentTypeId),
  );

  // Filter policy document types to only those that have documents
  return policyDocumentTypes.value.filter((type) => documentTypeIdsInDocuments.has(type.id));
});

// Count documents per document type (based on all account documents)
const documentTypeCounts = computed(() => {
  const counts = new Map<number, number>();
  allAccountDocuments.value
    .filter((doc) => doc.documentDomainName === 'Policy' && doc.documentTypeId)
    .forEach((doc) => {
      const currentCount = counts.get(doc.documentTypeId!) || 0;
      counts.set(doc.documentTypeId!, currentCount + 1);
    });
  return counts;
});

const documentTypeOptions = computed(() => {
  return availableDocumentTypes.value.map((type) => {
    const count = documentTypeCounts.value.get(type.id) || 0;
    return {
      label: type.name,
      description: `${count} document${count === 1 ? '' : 's'}`,
      value: type.id.toString(),
    };
  });
});

const filteredDocuments = computed(() => {
  const accountIdNum = selectedAccountId.value ? parseInt(selectedAccountId.value) : null;
  const docTypeIdNum = selectedDocumentTypeId.value ? parseInt(selectedDocumentTypeId.value) : null;

  return documents.value.filter((doc) => {
    // Filter by Policy domain
    if (doc.documentDomainName !== 'Policy') return false;

    // Filter by account if selected
    if (accountIdNum !== null && doc.accountId !== accountIdNum) {
      return false;
    }

    // Filter by document type if selected
    if (docTypeIdNum !== null && doc.documentTypeId !== docTypeIdNum) {
      return false;
    }

    return true;
  });
});

// Methods
const loadAccounts = async () => {
  isLoadingAccounts.value = true;
  try {
    accounts.value = await accountService.list();
  } catch (error) {
    console.error('Failed to load accounts:', error);
  } finally {
    isLoadingAccounts.value = false;
  }
};

const loadDocumentTypes = async () => {
  isLoadingDocumentTypes.value = true;
  try {
    documentTypes.value = await documentService.getDocumentTypes();
  } catch (error) {
    console.error('Failed to load document types:', error);
  } finally {
    isLoadingDocumentTypes.value = false;
  }
};

const loadDocuments = async () => {
  if (!selectedAccountId.value) {
    documents.value = [];
    allAccountDocuments.value = [];
    return;
  }

  isLoadingDocuments.value = true;
  try {
    const accountIdNum = parseInt(selectedAccountId.value);

    // Get Policy domain ID
    const domains = await documentService.getDocumentDomains();
    const policyDomain = domains.find((d) => d.name === 'Policy');

    // Always load all Policy documents for the account (for document type filtering)
    const allDocsRequest: { documentDomainId?: number; accountId?: number } = {
      accountId: accountIdNum,
    };
    if (policyDomain) {
      allDocsRequest.documentDomainId = policyDomain.id;
    }
    allAccountDocuments.value = await documentService.getDocuments(allDocsRequest);

    // Apply document type filter if selected
    if (selectedDocumentTypeId.value) {
      const docTypeIdNum = parseInt(selectedDocumentTypeId.value);
      documents.value = allAccountDocuments.value.filter((doc) => doc.documentTypeId === docTypeIdNum);
    } else {
      documents.value = allAccountDocuments.value;
    }
  } catch (error) {
    console.error('Failed to load documents:', error);
    documents.value = [];
    allAccountDocuments.value = [];
  } finally {
    isLoadingDocuments.value = false;
  }
};

const handleAccountSelect = (value: string) => {
  selectedAccountId.value = value || '';
  selectedDocumentTypeId.value = ''; // Reset document type when account changes
  loadDocuments();
};

const handleDocumentTypeSelect = (value: string) => {
  selectedDocumentTypeId.value = value || '';
  // Filter documents locally instead of reloading from API
  if (!selectedDocumentTypeId.value) {
    documents.value = allAccountDocuments.value;
  } else {
    const docTypeIdNum = parseInt(selectedDocumentTypeId.value);
    documents.value = allAccountDocuments.value.filter((doc) => doc.documentTypeId === docTypeIdNum);
  }
};

const resetDocumentTypeFilter = () => {
  selectedDocumentTypeId.value = '';
  documents.value = allAccountDocuments.value;
};

const handleDocumentClick = (doc: PlatformDocument) => {
  const document: Document = {
    name: doc.displayName || doc.fileName,
    documentId: doc.documentId, // UUID string from Document.DocumentID
    accountId: doc.accountId || undefined,
    displayName: doc.displayName,
    documentTypeName: doc.documentTypeName,
    createdAt: doc.createdByDate,
    type: doc.fileName.split('.').pop() || undefined,
  };

  // Toggle selection if clicking the same document
  if (props.selectedDocument?.documentId === document.documentId) {
    emit('documentSelect', null);
  } else {
    emit('documentSelect', document);
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getFileIcon = (fileName?: string) => {
  if (!fileName) return '📄';
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return '📄';
  if (ext === 'docx' || ext === 'doc') return '📝';
  if (ext === 'txt') return '📄';
  return '📁';
};

const isDocumentSelected = (doc: PlatformDocument) => {
  return props.selectedDocument?.documentId === doc.documentId;
};

const openDocument = async (doc: PlatformDocument, event: Event) => {
  event.stopPropagation();

  if (!doc.documentId || !doc.accountId) {
    console.error('Document ID and Account ID are required for viewing');
    return;
  }

  viewDocumentFileName.value = doc.displayName || doc.fileName;
  viewDocument.value = doc;
  showDocumentModal.value = true;
};

const fetchDocument = async (convertToPdf: boolean): Promise<string> => {
  if (!viewDocument.value?.documentId || !viewDocument.value?.accountId) {
    throw new Error('No document selected for viewing');
  }

  try {
    return await documentService.downloadDocumentFile(viewDocument.value.accountId, viewDocument.value.documentId, convertToPdf);
  } catch (error: any) {
    console.error('Failed to fetch document for viewing:', error);
    throw error;
  }
};

// When a platform document is pre-selected (e.g. from "Open in Playground"), sync account and load documents
watch(
  () => props.selectedDocument,
  (doc) => {
    if (doc?.accountId && doc.documentId && selectedAccountId.value !== String(doc.accountId)) {
      selectedAccountId.value = String(doc.accountId);
      loadDocuments();
    }
  },
  { immediate: true },
);

// Lifecycle
onMounted(async () => {
  await Promise.all([loadAccounts(), loadDocumentTypes()]);
});
</script>

<template>
  <div class="space-y-4">
    <!-- Account Dropdown -->
    <div class="space-y-2">
      <label class="text-sm font-medium text-slate-700">Client</label>
      <Combobox
        v-model="selectedAccountId"
        :options="accountOptions"
        placeholder="Search and select a client..."
        :disabled="isLoadingAccounts"
        @selected="(option) => handleAccountSelect(option.value)" />
    </div>

    <!-- Document Type Dropdown -->
    <div
      v-if="selectedAccountId"
      class="space-y-2">
      <label class="text-sm font-medium text-slate-700">Document Type</label>
      <div class="flex flex-row items-center gap-2">
        <UISelect
          v-model="selectedDocumentTypeId"
          :options="documentTypeOptions"
          placeholder="Select a document type"
          :loading="isLoadingDocumentTypes"
          class="flex-1"
          @update:modelValue="handleDocumentTypeSelect" />
        <button
          v-if="selectedDocumentTypeId"
          @click="resetDocumentTypeFilter"
          class="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          title="Clear document type filter">
          <X class="h-4 w-4" />
        </button>
      </div>
    </div>

    <!-- Document List -->
    <div
      v-if="selectedAccountId"
      class="space-y-2">
      <div
        v-if="isLoadingDocuments"
        class="py-4 text-center text-sm text-slate-500">
        Loading documents...
      </div>
      <div
        v-else-if="filteredDocuments.length === 0"
        class="py-4 text-center text-sm text-slate-500">
        No documents found. Please select a different client or document type.
      </div>
      <div
        v-else
        class="space-y-2">
        <div class="flex items-center gap-1">
          <h4 class="text-sm font-medium text-slate-700">Available Documents</h4>
          <span class="text-xs font-normal text-slate-500">({{ filteredDocuments.length }})</span>
        </div>

        <div class="max-h-64 space-y-2 overflow-y-auto">
          <div
            v-for="doc in filteredDocuments"
            :key="doc.documentId"
            :class="[
              'group cursor-pointer rounded-lg border p-3 transition-all duration-200',
              isDocumentSelected(doc)
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
            ]"
            @click="handleDocumentClick(doc)">
            <div
              :title="doc.displayName || doc.fileName"
              class="flex items-center justify-between gap-4">
              <div class="flex min-w-0 flex-1 items-center gap-3">
                <span class="shrink-0 text-xl">{{ getFileIcon(doc.fileName) }}</span>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-medium text-slate-900">
                    {{ doc.displayName || doc.fileName }}
                  </p>
                  <div class="mt-1 flex flex-wrap items-center gap-2">
                    <Badge
                      v-if="doc.documentTypeName"
                      variant="outline"
                      class="text-xs">
                      {{ doc.documentTypeName }}
                    </Badge>
                    <span class="flex items-center gap-1 text-xs text-slate-500">
                      <Clock class="h-3 w-3" />
                      {{ formatDate(doc.createdByDate) }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="flex shrink-0 items-center gap-2">
                <Badge
                  v-if="isDocumentSelected(doc)"
                  variant="secondary">
                  Selected
                </Badge>
                <button
                  v-if="doc.fileName.endsWith('.pdf') || doc.fileName.endsWith('.docx')"
                  @click="openDocument(doc, $event)"
                  class="rounded-md p-1 text-slate-500 opacity-0 transition-all duration-200 group-hover:opacity-100 hover:bg-blue-50 hover:text-blue-600"
                  title="View document">
                  <Eye class="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="!selectedAccountId"
      class="py-8 text-center text-sm text-slate-500">
      Please select a client to view available documents.
    </div>
  </div>

  <DocumentViewerModal
    :show="showDocumentModal"
    :fileName="viewDocumentFileName"
    :fetchDocument="fetchDocument"
    @update:show="showDocumentModal = $event"
    @close="showDocumentModal = false" />
</template>
